import { Link, useParams } from 'react-router-dom';
import { ReminderCadence } from '@prisma/client';
import { Breadcrumb, Button, Checkbox, Form, Layout, Select, Spin, Typography } from 'antd';

import { SourceAccessItem } from '@app/domain-access-interfaces';
import { FeatureFlags, PageLayout } from '@app/web-components';

import { AccessSelect, NotFoundPage } from '../../../common';
import { useFindAccessById, useUpsertFacelessAudit } from '../data';

export const FacelessAuditPage = () => {
  const { id } = useParams();
  const accessId = Number(id) || 0;

  const { data: access, isLoading } = useFindAccessById(accessId, { staleTime: Infinity });

  const { getBreadcrumbProps, getContentProps, getTitleProps } = PageLayout.useGetPageLayout('form');
  const [form] = Form.useForm();
  const create = useUpsertFacelessAudit(accessId);

  const { isFeatureEnabled } = FeatureFlags.useFeatureFlags();

  if (!isFeatureEnabled('faceless_audit')) {
    return <NotFoundPage />;
  }

  if (isLoading) {
    return <Spin style={{ width: '100%' }} />;
  }

  if (!access) {
    return <NotFoundPage />;
  }

  return (
    <>
      <Breadcrumb
        {...getBreadcrumbProps({
          items: [
            { title: 'Admin' },
            { title: <Link to="../">Faceless Audit</Link> },
            { title: 'Create Faceless Audit Map' },
          ],
        })}
      />
      <Layout.Content {...getContentProps()}>
        <Typography.Title {...getTitleProps()}>Create Faceless Audit Map</Typography.Title>
        <Form
          initialValues={{
            enabled: access.accessFacelessAudit?.enabled ?? false,
            cadence: access.accessFacelessAudit?.cadence,
            dependencies: access.accessFacelessAudit?.mappedAccesses ?? [],
          }}
          layout="vertical"
          form={form}
          onFinish={({ dependencies, ...data }) =>
            create.mutate({
              data,
              dependencies: dependencies.map(({ externalId, accessSource }: SourceAccessItem) => ({
                externalId,
                accessSource,
              })),
            })
          }
        >
          <Form.Item
            label="Access"
            tooltip="The access to be audited. SAAF will check if the users of faceless accounts subscribed to this account have the required dependencies selected below."
          >
            {access?.name}
          </Form.Item>
          <Form.Item label="Enabled" name="enabled" valuePropName="checked">
            <Checkbox />
          </Form.Item>
          <Form.Item
            label="Dependencies"
            name="dependencies"
            required
            rules={[{ required: true }]}
            tooltip="Select accesses whose membership is required to maintain eligibility for the audited access."
          >
            <AccessSelect agsOnly />
          </Form.Item>
          <Form.Item
            label="Cadence"
            name="cadence"
            required
            rules={[{ required: true }]}
            tooltip="Cadence for sending faceless audit summary."
          >
            <Select
              options={[
                { value: ReminderCadence.DAILY },
                { value: ReminderCadence.WEEKLY },
                { value: ReminderCadence.BIWEEKLY },
                { value: ReminderCadence.MONTHLY },
                { value: ReminderCadence.QUARTERLY },
              ]}
            />
          </Form.Item>
          <Button htmlType="submit">Save</Button>
        </Form>
      </Layout.Content>
    </>
  );
};
